/* ==========================================================
   BARROS THEME — BRANDS SHOWCASE
   ========================================================== */

class BarrosBrandsShowcase {
  constructor(root) {
    this.root = root;

    this.triggers = Array.from(
      root.querySelectorAll('[data-barros-brand-trigger]')
    );

    this.scenes = Array.from(
      root.querySelectorAll('[data-barros-brand-scene]')
    );

    this.activeIndex = 0;
    this.hoverTimer = null;
    this.isDesktop = window.matchMedia('(min-width: 750px)');

    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);

    this.init();
  }

  init() {
    if (!this.root || !this.triggers.length || !this.scenes.length) {
      return;
    }

    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener('mouseenter', () => {
        if (!this.isDesktop.matches) return;

        this.scheduleChange(index);
      });

      trigger.addEventListener('focus', () => {
        this.setActive(index);
      });

      trigger.addEventListener('click', () => {
        this.setActive(index);
      });
    });

    this.root.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);

    this.setActive(0, false);
  }

  prepareScene(index) {
    const scene = this.scenes[index];
    if (!scene) return;

    const template = scene.querySelector('[data-barros-brand-media-template]');
    if (!template) return;

    // Images inside <template> are not downloaded until the brand is requested.
    // Decode begins on hover/focus, before the scene becomes visible.
    template.content.querySelectorAll('img').forEach((image) => {
      image.loading = 'eager';
    });
    template.replaceWith(template.content);
  }

  scheduleChange(index) {
    window.clearTimeout(this.hoverTimer);
    this.prepareScene(index);

    this.hoverTimer = window.setTimeout(() => {
      this.setActive(index);
    }, 90);
  }

  setActive(index, animate = true) {
    if (
      index < 0 ||
      index >= this.triggers.length ||
      index >= this.scenes.length
    ) {
      return;
    }

    this.prepareScene(index);
    this.activeIndex = index;

    this.triggers.forEach((trigger, triggerIndex) => {
      const isActive = triggerIndex === index;

      trigger.classList.toggle('is-active', isActive);
      if (isActive) {
        trigger.setAttribute('aria-current', 'true');
      } else {
        trigger.removeAttribute('aria-current');
      }
    });

    this.scenes.forEach((scene, sceneIndex) => {
      const isActive = sceneIndex === index;

      scene.classList.toggle('is-active', isActive);
      scene.setAttribute(
        'aria-hidden',
        isActive ? 'false' : 'true'
      );

      if (!animate && isActive) {
        scene.style.transition = 'none';

        window.requestAnimationFrame(() => {
          scene.style.removeProperty('transition');
        });
      }
    });
  }

  onKeydown(event) {
    if (!this.triggers.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.moveFocus(1);
      return;
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.moveFocus(-1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      this.focusTrigger(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      this.focusTrigger(this.triggers.length - 1);
    }
  }

  moveFocus(direction) {
    const focusedIndex = this.triggers.findIndex(
      (trigger) => trigger === document.activeElement
    );

    const startIndex =
      focusedIndex >= 0 ? focusedIndex : this.activeIndex;

    const nextIndex =
      (startIndex + direction + this.triggers.length) %
      this.triggers.length;

    this.focusTrigger(nextIndex);
  }

  focusTrigger(index) {
    const trigger = this.triggers[index];

    if (!trigger) return;

    this.setActive(index);
    trigger.focus();
  }

  onResize() {
    window.clearTimeout(this.hoverTimer);
  }

  destroy() {
    this.root.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    window.clearTimeout(this.hoverTimer);
  }
}

function initBarrosBrandsShowcases(scope = document) {
  const showcases = scope.querySelectorAll('[data-barros-brands]');

  showcases.forEach((showcase) => {
    if (showcase.dataset.barrosBrandsInitialized === 'true') {
      return;
    }

    showcase.dataset.barrosBrandsInitialized = 'true';
    showcase.barrosBrandsShowcase = new BarrosBrandsShowcase(showcase);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initBarrosBrandsShowcases();
});

document.addEventListener('shopify:section:load', (event) => {
  initBarrosBrandsShowcases(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const showcases = event.target.querySelectorAll('[data-barros-brands]');

  showcases.forEach((showcase) => {
    if (showcase.barrosBrandsShowcase) {
      showcase.barrosBrandsShowcase.destroy();
    }
  });
});